/**
 * StarPrint service — the single entry point to `@am3nd-org/capacitor-starprint-plugin`.
 *
 * This is the ONLY module that imports the plugin. UI and stores talk to the
 * exported `starPrintService` singleton, never to the plugin directly.
 *
 * The new plugin is connection-oriented (discover → connect → connectionId →
 * print/status/drawer by connectionId → disconnect), unlike the old stateless
 * Cordova plugin. This service owns one active connection at a time, lazily
 * reconnects from the persisted default printer, and recovers from a stale
 * connection with a single reconnect + retry.
 *
 * All plugin calls are serialized through the shared `withLock` mutex
 * (`src/utils/printer-lock.ts`) — the same lock the legacy path uses — so the
 * old 30s readiness poller and the new path can never hit the hardware at once.
 */
import { Capacitor } from '@capacitor/core';
import { StarPrint } from '@am3nd-org/capacitor-starprint-plugin';

import { withLock } from 'src/utils/printer-lock';
import {
  saveDefaultStarPrinterToDB,
  getDefaultStarPrinterFromDB,
  deleteDefaultStarPrinterFromDB,
} from 'src/utils/Services/DB_Utils';

import { DEFAULT_DISCOVERY_INTERFACES } from './star-print.types';
import type {
  StarPrinter,
  SavedPrinter,
  StarPrintResult,
  StarPrinterStatus,
  StarPrinterInterface,
  StarPrintReceiptItem,
  StarPrinterConnection,
  PrinterDiscoveryResult,
  DiscoverPrintersResult,
  NativeAvailabilityResult,
  StarPrintConfigurationIssue,
  BluetoothAuthorizationResult,
} from './star-print.types';

// Per-call timeouts, kept under the lock's 30s auto-release so a single
// connect+print sequence cannot be interrupted mid-operation.
const DISCOVERY_TIMEOUT_MS = 8_000;
const CONNECT_TIMEOUT_MS = 10_000;
const OP_TIMEOUT_MS = 15_000;

// Error codes/messages that mean "the connection went away" → reconnect once.
const STALE_CODES = new Set([
  'NOT_CONNECTED',
  'not_connected',
  'CONNECTION_FAILED',
  'connection_failed',
]);

class StarServiceError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'StarServiceError';
    this.code = code;
  }
}

function isStaleError(errLike: unknown): boolean {
  if (!errLike) return false;
  const obj = errLike as { code?: unknown; message?: unknown };
  const code = typeof obj.code === 'string' ? obj.code : '';
  const message = typeof obj.message === 'string' ? obj.message : String(errLike);
  if (STALE_CODES.has(code)) return true;
  return /not connected|fail to open port|disconnected/i.test(message);
}

function toError(result: StarPrintResult, fallback: string): StarServiceError {
  const message = result.error?.message || result.message || fallback;
  return new StarServiceError(result.error?.code ?? 'UNKNOWN', message);
}

/**
 * Map a discovered `StarPrinter` onto the persisted `SavedPrinter` shape. The
 * full `connection` is kept so the service can reconnect headlessly (LAN needs
 * ip/port, Bluetooth the MAC, USB the port name).
 */
export function toSavedPrinter(printer: StarPrinter): SavedPrinter {
  return {
    identifier: printer.identifier,
    model: printer.model,
    name: printer.name ?? printer.modelName,
    interfaceType: printer.interfaceType,
    connection: printer.connection,
    savedAt: new Date().toISOString(),
    schemaVersion: 1,
  };
}

export interface DiscoverOptions {
  interfaceTypes?: StarPrinterInterface[];
  timeoutMs?: number;
  includeCached?: boolean;
}

export interface PrintReceiptOptions {
  cutPaper?: boolean;
  openCashDrawer?: boolean;
  copies?: number;
  jobId?: string;
}

export interface PrintImageOptions {
  cutPaper?: boolean;
  /** Print width in dots; defaults to the printer's paper width when omitted. */
  width?: number;
  openCashDrawer?: boolean;
  jobId?: string;
}

function createStarPrintService() {
  // ── Connection state ──────────────────────────────────────────────────────
  let activeConnectionId: string | null = null;
  let activePrinter: StarPrinter | null = null;
  let lastStatus: StarPrinterStatus | null = null;
  let pluginListenersRegistered = false;
  const statusListeners = new Set<(status: StarPrinterStatus) => void>();

  const emit = (status: StarPrinterStatus): void => {
    statusListeners.forEach((cb) => {
      try {
        cb(status);
      } catch (err) {
        console.error('[StarPrintService] status listener threw:', err);
      }
    });
  };

  // ── Internals (no locking — callers hold the lock) ────────────────────────
  const ensurePluginListeners = async (): Promise<void> => {
    if (pluginListenersRegistered) return;
    pluginListenersRegistered = true;
    await StarPrint.addListener('printerStatusChanged', (event) => {
      if (event.connectionId) activeConnectionId = event.connectionId;
      lastStatus = event.status;
      emit(event.status);
    });
    await StarPrint.addListener('printerDisconnected', () => {
      activeConnectionId = null;
      activePrinter = null;
    });
  };

  const readStatus = async (connectionId: string): Promise<StarPrinterStatus> => {
    const status = await StarPrint.getStatus({ connectionId, timeoutMs: OP_TIMEOUT_MS });
    lastStatus = status;
    emit(status);
    return status;
  };

  const connectRaw = async (connection: StarPrinterConnection): Promise<StarPrintResult> => {
    const result = await StarPrint.connect({
      ...connection,
      timeoutMs: connection.timeoutMs ?? CONNECT_TIMEOUT_MS,
    });
    if (result.success && result.connectionId) {
      activeConnectionId = result.connectionId;
      activePrinter = result.printer ?? activePrinter;
      if (result.status) {
        lastStatus = result.status;
        emit(result.status);
      }
      await ensurePluginListeners();
    }
    return result;
  };

  const ensureConnectedRaw = async (): Promise<string> => {
    if (activeConnectionId) return activeConnectionId;
    const saved = await getDefaultStarPrinterFromDB();
    if (!saved) {
      throw new StarServiceError('NOT_CONNECTED', 'No default printer configured');
    }
    const result = await connectRaw(saved.connection);
    if (!result.success || !result.connectionId) {
      throw toError(result, 'Could not connect to the saved printer');
    }
    return result.connectionId;
  };

  /** Run an op against the active connection; on a stale connection, reconnect once and retry once. */
  const withConnectionRaw = async (
    run: (connectionId: string) => Promise<StarPrintResult>
  ): Promise<StarPrintResult> => {
    const id = await ensureConnectedRaw();
    try {
      const result = await run(id);
      if (!result.success && isStaleError(result.error)) {
        activeConnectionId = null;
        return await run(await ensureConnectedRaw());
      }
      return result;
    } catch (err) {
      if (isStaleError(err)) {
        activeConnectionId = null;
        return run(await ensureConnectedRaw());
      }
      throw err;
    }
  };

  const disconnectRaw = async (): Promise<void> => {
    const id = activeConnectionId;
    activeConnectionId = null;
    activePrinter = null;
    if (!id) return;
    try {
      await StarPrint.disconnect({ connectionId: id });
    } catch (err) {
      // Disconnect may fail if the connection is already gone — safe to ignore.
      console.warn('[StarPrintService] disconnect failed (ignored):', err);
    }
  };

  // ── Public surface ────────────────────────────────────────────────────────
  return {
    /**
     * Discover reachable printers (defaults to LAN + Bluetooth; BLE/USB are opt-in)
     * plus any per-interface `configurationIssues` the native layer reports (e.g. a
     * skipped interface or a permission problem), so callers can explain an empty result.
     */
    discoverPrinters(opts: DiscoverOptions = {}): Promise<PrinterDiscoveryResult> {
      return withLock(async () => {
        const result = await StarPrint.discoverPrinters({
          interfaceTypes: opts.interfaceTypes ?? DEFAULT_DISCOVERY_INTERFACES,
          timeoutMs: opts.timeoutMs ?? DISCOVERY_TIMEOUT_MS,
          includeCached: opts.includeCached,
        });
        // The native layer returns `configurationIssues` on the discovery result, but the
        // plugin's TS `DiscoverPrintersResult` doesn't declare the field yet — read it via a cast.
        const issues =
          (
            result as DiscoverPrintersResult & {
              configurationIssues?: StarPrintConfigurationIssue[];
            }
          ).configurationIssues ?? [];
        return { printers: result.printers, configurationIssues: issues, message: result.message };
      });
    },

    /** Connect to a specific printer (used by the settings UI when picking one). */
    connectPrinter(
      connection: StarPrinterConnection
    ): Promise<{ connectionId: string; printer?: StarPrinter; status?: StarPrinterStatus }> {
      return withLock(async () => {
        const result = await connectRaw(connection);
        if (!result.success || !result.connectionId) {
          throw toError(result, 'Failed to connect to printer');
        }
        return {
          connectionId: result.connectionId,
          printer: result.printer,
          status: result.status,
        };
      });
    },

    /** Drop the active connection (best-effort) and clear cached state. */
    disconnectPrinter(): Promise<void> {
      return withLock(() => disconnectRaw());
    },

    /** Connection id of the live connection, or null. Synchronous, for UI hints. */
    getActiveConnectionId(): string | null {
      return activeConnectionId;
    },

    /** Read printer status, reconnecting from the saved default if needed. */
    getPrinterStatus(opts: { forceReconnect?: boolean } = {}): Promise<StarPrinterStatus> {
      return withLock(async () => {
        if (opts.forceReconnect) await disconnectRaw();
        try {
          return await readStatus(await ensureConnectedRaw());
        } catch (err) {
          if (isStaleError(err)) {
            activeConnectionId = null;
            return readStatus(await ensureConnectedRaw());
          }
          throw err;
        }
      });
    },

    /** Last status seen via getPrinterStatus or a status-changed event. */
    getLastStatus(): StarPrinterStatus | null {
      return lastStatus;
    },

    /**
     * Subscribe to live status changes. Registers the plugin listeners lazily and
     * immediately replays the last known status. Returns an unsubscribe function.
     */
    async addStatusListener(cb: (status: StarPrinterStatus) => void): Promise<() => void> {
      statusListeners.add(cb);
      await ensurePluginListeners();
      if (lastStatus) cb(lastStatus);
      return () => {
        statusListeners.delete(cb);
      };
    },

    /** Print a receipt built from `StarPrintReceiptItem[]`. */
    printReceipt(
      items: StarPrintReceiptItem[],
      opts: PrintReceiptOptions = {}
    ): Promise<StarPrintResult> {
      return withLock(() =>
        withConnectionRaw((connectionId) =>
          StarPrint.printReceipt({
            connectionId,
            items,
            cutPaper: opts.cutPaper ?? true,
            openCashDrawer: opts.openCashDrawer,
            copies: opts.copies,
            jobId: opts.jobId,
            timeoutMs: OP_TIMEOUT_MS,
          })
        )
      );
    },

    /**
     * Print a pre-rendered bitmap (a base64 PNG/JPEG, WITHOUT the `data:` URL
     * prefix). Mirrors `printReceipt`: serialized through the lock and routed at
     * the active connection, reconnecting+retrying once on a stale connection.
     */
    printImage(imageBase64: string, opts: PrintImageOptions = {}): Promise<StarPrintResult> {
      return withLock(() =>
        withConnectionRaw((connectionId) =>
          StarPrint.printImage({
            connectionId,
            imageBase64,
            width: opts.width,
            cutPaper: opts.cutPaper ?? true,
            openCashDrawer: opts.openCashDrawer,
            jobId: opts.jobId,
            timeoutMs: OP_TIMEOUT_MS,
          })
        )
      );
    },

    /** Kick the cash drawer. */
    openCashDrawer(opts: { channel?: 'drawer1' | 'drawer2' } = {}): Promise<StarPrintResult> {
      return withLock(() =>
        withConnectionRaw((connectionId) =>
          StarPrint.openCashDrawer({
            connectionId,
            channel: opts.channel,
            timeoutMs: OP_TIMEOUT_MS,
          })
        )
      );
    },

    /** Persist `printer` as the default (stores the full connection for reconnect). */
    async saveDefaultPrinter(printer: StarPrinter): Promise<void> {
      await saveDefaultStarPrinterToDB(toSavedPrinter(printer));
    },

    /** Read the persisted default printer, or null. */
    getDefaultPrinter(): Promise<SavedPrinter | null> {
      return getDefaultStarPrinterFromDB();
    },

    /** Forget the default printer and disconnect. */
    async clearDefaultPrinter(): Promise<void> {
      await deleteDefaultStarPrinterFromDB();
      await withLock(() => disconnectRaw());
    },

    /** Whether the native StarIO10 SDK is linked on this platform. */
    isNativeAvailable(): Promise<NativeAvailabilityResult> {
      return StarPrint.isNativeAvailable();
    },

    /**
     * Read OS Bluetooth authorization SILENTLY — never instantiates a Bluetooth
     * manager, so it does not trigger the iOS permission prompt. No `withLock`:
     * it touches no connection and contends with nothing.
     */
    getBluetoothAuthorization(): Promise<BluetoothAuthorizationResult> {
      return StarPrint.getBluetoothAuthorization();
    },

    /** Dev-only: drive the web mock's reported status. No-op on native. */
    async setWebMockStatus(
      status: 'online' | 'offline' | 'paper_empty' | 'cover_open'
    ): Promise<void> {
      if (Capacitor.getPlatform() !== 'web') return;
      await StarPrint.setMockMode({ status });
    },
  };
}

export const starPrintService = createStarPrintService();
